
import React, { useState } from 'react';
import { useSplash } from '../../../contexts/SplashContext';

const JobOrderPrint = ({ data }) => { 
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    // Helper function to get translated text
    const getTranslatedText = (textObj, langCode = selectedLanguage) => {
        if (!textObj) return '';
        if (typeof textObj === 'string') return textObj;
        return textObj[langCode] || textObj[defaultLanguage] || Object.values(textObj)[0] || '';
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Helper function to format currency
    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return parseFloat(amount).toFixed(2);
    };

    return (
        <div className="job-order-print">
            {/* Header */}
            <div className="job-order-print-header">
                <div>
                    <div className="job-order-print-company">
                        AL SHAKAR TAILORING W.L.L
                    </div>
                    <div className="job-order-print-title">
                        JOB ORDER
                    </div>
                </div>
                <div className="job-order-print-meta">
                    <div>{formatDate(new Date())}</div>
                    <div>{new Date().toLocaleTimeString('en-GB', { 
                        hour: '2-digit', 
                        minute: '2-digit', 
                        second: '2-digit',
                        hour12: false 
                    })}</div>
                </div>
            </div>

            {/* Job Order Details */}
            <div className="job-order-print-section">
                <div className="job-order-print-row">
                    {/* Left Side */}
                    <div className="job-order-print-col">
                        <div className="job-order-print-detail-row">
                            <div className="job-order-print-label">JOB ORDER NO:</div>
                            <div className="job-order-print-value">{data.order_number || 'N/A'}</div>
                        </div>
                        <div className="job-order-print-detail-row">
                            <div className="job-order-print-label">JOB ORDER DATE:</div>
                            <div className="job-order-print-value">{formatDate(data.order_date)}</div>
                        </div>
                        <div className="job-order-print-detail-row">
                            <div className="job-order-print-label">CUSTOMER NAME:</div>
                            <div className="job-order-print-value">{getTranslatedText(data.customer_name)}</div>
                        </div>
                    </div>

                    {/* Right Side */}
                    <div className="job-order-print-col">
                        <div className="job-order-print-detail-row">
                            <div className="job-order-print-label">BRANCH NAME:</div>
                            <div className="job-order-print-value">{data.branch_name || 'N/A'}</div>
                        </div>
                        <div className="job-order-print-detail-row">
                            <div className="job-order-print-label">TRIAL DATE:</div>
                            <div className="job-order-print-value">{formatDate(data.trial_date)}</div>
                        </div>
                        <div className="job-order-print-detail-row">
                            <div className="job-order-print-label">DUE DATE:</div>
                            <div className="job-order-print-value">{formatDate(data.due_date)}</div>
                        </div>
                    </div>
                </div>
                <div className="job-order-print-row">
                    {/* Left Side */}
                    <div className="job-order-print-col">
                        <div className="job-order-print-detail-row">
                            <div className="job-order-print-label-wide">DETAILED STYLE INFORMATION:</div>
                            <div className="job-order-print-value">{getTranslatedText(data.order_detail_info)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Separator Line */}
            <hr className="job-order-print-hr" />

            {/* Items/Description Section */}
            <div className="job-order-print-section">
                <div className="job-order-print-items-header">
                    <div>No</div>
                    <div>Description</div>
                    <div>Quantity</div>
                    <div>Special Description</div>
                </div>
                {data.items && data.items.length > 0 ? (
                    data.items.map((item, index) => (
                        <div key={index} className="job-order-print-item-row">
                            <div>{item.item?.item_code || 'N/A'}</div>
                            <div>{item.item?.label || 'N/A'}</div>
                            <div>{item.quantity || '0'}</div>
                            <div> {item.item_description || 'N/A'} </div>
                        </div>
                    ))
                ) : (
                    <div>No items found</div>
                )}
            </div>

            {/* Separator Line */}
            <hr className="job-order-print-hr" />

            {/* Measurements Section */}
            <div>
                <div className="job-order-print-section-title">
                    MEASUREMENTS
                </div>

                {/* Trouser Measurements */}
                <div className="job-order-print-subsection">
                    <div className="job-order-print-subsection-title">
                        TROUSER
                    </div>
                    <div className="job-order-print-measurements">
                        {[ 
                            'waist_measurement', 
                            'hip_measurement', 
                            'waist_line_to_heel', 
                            'thigh_measurement', 
                            'knee_measurement', 
                            'bottom_measurement', 
                            'waist_to_crotch'
                        ].map((key, idx, arr) => {
                            const val = data.trouser?.[key];
                            const num = (val === undefined || val === null || val === '') ? 0 : parseFloat(val);
                            return (
                                (num.toFixed(2)) + (idx < arr.length - 1 ? ', ' : '')
                            );
                        })}
                    </div>
                </div>

                {/* Additional Measurements */}
                <div className="job-order-print-additional">
                    {(() => {
                        let backCondition = '';
                        if (data.trouser?.back_condition === 'narrow_back') {
                            backCondition = 'Erract Narrow Back Head Forward Flat Chest';
                        } else if (data.trouser?.back_condition === 'round_back') {
                            backCondition = 'Round Back Head Forward Flat Chest';
                        } else if (data.trouser?.back_condition === 'full_chest') {
                            backCondition = 'Full Chest And Stomach Hollow Back';
                        }
                        if (backCondition !== '') {
                            return (
                                <div className="job-order-print-measurements">
                                    Back Condition: {backCondition}
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>

                {/* Separator Line */}
                <hr className="job-order-print-hr-sm" />

                {/* Shirt/Jacket Measurements */}
                <div className="job-order-print-subsection">
                    <div className="job-order-print-subsection-title">
                        SHIRT / JACKET
                    </div>
                    <div className="job-order-print-measurements">
                        {[ 
                            'shoulder_seam_to_first_bone', 
                            'around_chest', 
                            'around_waist', 
                            'around_hip', 
                            'tip_to_tip_shoulder', 
                            'center_back_sleeve_seam', 
                            'shoulder_tip_to_below_wrist', 
                            'color_seam_natural_waist', 
                            'shoulder_seam_mid_rise', 
                            'sleeve_length', 
                            'neck_around'
                        ].map((key, idx, arr) => {
                            const val = data.jacket?.[key];
                            const num = (val === undefined || val === null || val === '') ? 0 : parseFloat(val);
                            return (
                                (num.toFixed(2)) + (idx < arr.length - 1 ? ', ' : '')
                            );
                        })}
                    </div>
                </div>

                {/* Additional Measurements */}
                <div className="job-order-print-additional">
                    {(() => {
                        let shoulderType = '';
                        if (data.jacket?.shoulder_type === 'long_neck') {
                            shoulderType = 'Sloping Should Long Neck';
                        } else if (data.jacket?.shoulder_type === 'regular_shoulder') {
                            shoulderType = 'Sloping Regular Shoulder';
                        } else if (data.jacket?.shoulder_type === 'square_shoulder') {
                            shoulderType = 'Square Shoulder';
                        }
                        if (shoulderType !== '') {
                            return (
                                <div className="job-order-print-measurements">
                                    Shoulder Type: {shoulderType}
                                </div>
                            );
                        }
                        return null;
                    })()}

                    {(() => {
                        let chestType = '';
                        if (data.jacket?.chest_type === 'narrow_back_proment') {
                            chestType = 'Erract Narrow Back Proment Chest';
                        } else if (data.jacket?.chest_type === 'forward_flat_chest') {
                            chestType = 'Round Back Head Forward Flat Chest';
                        } else if (data.jacket?.chest_type === 'stomach_hollow_back') {
                            chestType = 'Full Chest And Stomach Hollow Back';
                        }
                        if (chestType !== '') {
                            return (
                                <div className="job-order-print-measurements">
                                    Chest Type: {chestType}
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
            </div>
        </div>
    );
}; 

export default JobOrderPrint;